import React from "react";
import { motion } from "framer-motion";
import { Droplet, Cpu, FileBarChart2 } from "lucide-react";

const steps = [
  {
    n: "01",
    icon: Droplet,
    title: "Prélèvement sanguin",
    desc: "Quelques microlitres de sang suffisent. Le test s'effectue directement sur la plateforme MeMed Key®, au point de soin.",
  },
  {
    n: "02",
    icon: Cpu,
    title: "Analyse de la réponse immunitaire",
    desc: "Mesure simultanée de trois protéines clés — TRAIL, IP-10 et CRP — combinées par un algorithme propriétaire validé.",
  },
  {
    n: "03",
    icon: FileBarChart2,
    title: "Score en ~15 minutes",
    desc: "Un score objectif indiquant la probabilité d'une infection bactérienne ou virale, prêt à éclairer la décision clinique.",
  },
];

const HowItWorks = () => {
  return (
    <section
      id="how-it-works"
      data-testid="how-it-works-section"
      className="relative py-24 md:py-32 bg-[#0A1128]/60"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        <div className="max-w-2xl mb-16 nx-fade">
          <div className="text-xs uppercase tracking-[0.2em] text-[#5BC4D1] font-medium mb-4">
            Comment fonctionne MeMed BV®
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-light leading-tight tracking-tight">
            Trois protéines.{" "}
            <span className="font-semibold">Une décision éclairée.</span>
          </h2>
          <p className="mt-5 text-slate-300 leading-relaxed">
            Contrairement aux tests qui recherchent directement un agent
            pathogène, MeMed BV® analyse la réponse immunitaire du patient
            — apportant une information objective lorsque la clinique
            reste ambiguë.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {steps.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.n}
                data-testid={`step-${i}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ delay: i * 0.12, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                className="relative bg-[#0E1530] border border-white/10 rounded-3xl p-8 md:p-10"
              >
                <div className="flex items-center justify-between">
                  <div className="font-display text-5xl text-white/10 font-light">
                    {s.n}
                  </div>
                  <div className="h-12 w-12 rounded-xl flex items-center justify-center bg-[#5BC4D1]/10 border border-[#5BC4D1]/20 text-[#5BC4D1]">
                    <Icon size={22} strokeWidth={1.6} />
                  </div>
                </div>
                <h3 className="mt-6 font-display text-2xl font-medium">
                  {s.title}
                </h3>
                <p className="mt-3 text-sm text-slate-400 leading-relaxed">
                  {s.desc}
                </p>
                {i < steps.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-6 h-px w-12 bg-gradient-to-r from-[#5BC4D1]/40 to-transparent" />
                )}
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
